import * as angular from 'angular';
import * as moment from 'moment';
import * as d3 from 'd3';
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { apsEnums } from "../../shared/dto/aps.enums";
import { apsEvents } from "../../shared/dto/aps.events";
import { EnumOption, UIUtilityService } from "../../services/utility/uiUtility.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import ShopServiceName, { ShopService } from "../../services/shops/shops.service";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import GobWeightsHubName, { GobWeightsHub, GobWeightsHubService } from "../../services/reports/gobWeights.hub";
import GobWeightsServiceName, { GobWeightsService } from "../../services/measures/gobWeights.service";
import AuthServiceName, { AuthService } from "../../services/auth/auth.service";
import EquipmentServiceName, { EquipmentService } from "../../services/equipment/equipment.service";
import AuthDataServiceName, { AuthDataService } from "../../services/auth/authData.service";
import NotifyServiceName, { NotifyService } from "../../services/notify/notify.service";
import GobWeightDetailName, { openGobWeightDetail } from "./gobWeightDetail.controller";
import * as _ from "lodash";

let requires = [ShopServiceName, LoaderServiceName, GobWeightsHubName, GobWeightsServiceName, AuthServiceName,
  EquipmentServiceName, AuthDataServiceName, NotifyServiceName, GobWeightDetailName];

interface DataPoint {
  x: Date;
  y: number;
}

interface Scale extends apsViewModels.EquipmentDetail {
  scaleConfig: any;
  modeName: string;
  availableModes: EnumOption[];
}

class GobWeightsGraphCtrl {
  width: number;
  height: number;
  padding: number = 20;
  shopId: string;
  isRelativeToNow: boolean = true;
  isScaleButtonVisible: boolean = true;
  hoursToShow: number;
  weightHourOptions: number[];
  timeZoneId: string;
  api: any;

  plantLocationId: string;

  private $scope: angular.IScope;
  private $interval: angular.IIntervalService;
  private chartIntervalPromise: angular.IPromise<void>;
  private chartUpdateInterval: number = 60000;

  private allowZoom: boolean = false;
  private chartInitialized: boolean = false;

  private shopService: ShopService;
  private loaderService: LoaderService;
  private gobWeightsHub: GobWeightsHub;
  private gobWeightsService: GobWeightsService;
  private authService: AuthService;
  private equipmentService: EquipmentService;
  private authDataService: AuthDataService;
  private notifyService: NotifyService;
  private scaleModes: EnumOption[];

  private $q: angular.IQService;
  private machineNameMap: any = {};

  private scales: Scale[];

  private xScale: any;
  private xScale1: any;
  private yScale: any;
  private scale: number = 1;
  private scaleExtent: [number, number] = [1, 10];
  private translate: [number, number] = [0, 0];
  private zoomLevel: number = 1;
  isScaleMode: boolean;
  private currentUser: apsViewModels.UserData;
  start: Date;
  end: Date;
  uiStart: Date;
  uiEnd: Date;
  private shop: apsViewModels.ShopConfiguration;
  private gobWeightsReport: apsViewModels.GobWeightsReport;
  private $modal: mm.foundation.modal;
  gobWeightsPretty: any[];
  chartOptions: any;

  constructor(
    gobWeightsService: GobWeightsService,
    loaderService: LoaderService,
    shopService: ShopService,
    $q: angular.IQService,
    $timeout: angular.ITimeoutService,
    uiUtilityService: UIUtilityService,
    gobWeightsHubService: GobWeightsHubService,
    $interval: angular.IIntervalService,
    $scope: angular.IScope, $modal: mm.foundation.modal,
    authService: AuthService,
    equipmentService: EquipmentService,
    authDataService: AuthDataService,
    notifyService: NotifyService
  ) {

    this.$scope = $scope;
    this.$modal = $modal;
    this.$interval = $interval;
    this.gobWeightsService = gobWeightsService;
    this.loaderService = loaderService;
    this.shopService = shopService;
    this.authService = authService;
    this.equipmentService = equipmentService;
    this.authDataService = authDataService;
    this.isScaleMode = this.authDataService.isScaleMode();
    this.notifyService = notifyService;
    this.scaleModes = uiUtilityService.getListOptionsFromEnum(apsEnums.ScaleMode, 1);

    this.$q = $q;

    $timeout(() => {
      this.gobWeightsHub = gobWeightsHubService.createGobWeightsHub(this.shopId,
        (w: apsViewModels.GobWeightItem) => this.addIncomingGobWeight(w),
        (c: apsEvents.GobWeightCommentAdded) => this.addIncomingGobWeightComment(c));

      this.weightHourOptions = [4, 24];
      this.xScale1 = d3.time.scale();

      this.chartOptions = {
        chart: {
          type: 'lineChart',
          useInteractiveGuideline: false,
          width: this.width || 1200,
          height: this.height || 500,
          margin: {
            top: 50,
            right: 50,
            bottom: 50,
            left: 75
          },
          pointSize: 20,
          x: w => w.x,
          y: w => w.y,
          tooltip: {
            contentGenerator: (d) => {
              let label = moment(this.getLocalTime(d.value)).format('ddd, MMM DD h:mmA');
              let formatted = `<table><thead><tr><td colspan="3"><strong>${label}</strong></td></tr></thead>`;
              formatted += `<tbody><tr><td class="legend-color-guide"><div style="background-color: ${d.series[0].color};"></div></td>`;
              formatted += `<td class="key">${d.series[0].key}</td><td class="value">${d.series[0].value}</td></tr></tbody></table>`;
              return formatted;
            }
          },
          xAxis: {
            axisLabel: 'Hour',
            showMaxMin: false,
            ticks: d3.time.hour,
            tickFormat: (d) => {
              return d3.time.format('%H')(this.getLocalTime(new Date(d)));
            }
          },
          xScale: this.xScale1,
          yAxis: {
            axisLabel: 'Weight (oz)',
            showMaxMin: false,
            tickFormat: d => {
              let digitsOfPrecision = this.zoomLevel >= 4 ? 3 : 2;
              return d.toFixed(digitsOfPrecision);
            }
          },
          title: {
            enabled: true,
            text: 'Gob Weight Samples, ' + this.getFriendlyTimespanName()
          },
          dispatch: {
            renderEnd: e => {
              if (!this.chartInitialized) {
                this.initZoom();
                this.formatChart();
                this.chartInitialized = true;
              }
            }
          },
          zoom: {
            enabled: this.allowZoom
          }
        }
      };

      this.initializeData();
    });

    $scope.$on('$destroy', () => {
      if (this.gobWeightsHub) {
        this.gobWeightsHub.leaveGroup(this.shopId)
          .then(() => this.gobWeightsHub.uninitialize());
      }
    });
  }

  initializeData() {
    this.shopService.getShopConfiguration(this.shopId)
      .then(shop => {
        let rawStart = moment().subtract(this.hoursToShow, 'hours');
        this.shop = shop;
        this.timeZoneId = shop.timeZoneId;
        this.uiStart = this.getPlantTime(rawStart.toDate());
        this.uiEnd = this.getPlantTime(moment().toDate());
        this.start = this.getLocalTime(this.uiStart);
        this.end = this.getLocalTime(this.uiEnd);

        _.each(this.shop.machineConfigurations, (m: apsViewModels.MachineConfiguration) => {
          this.machineNameMap[m.id] = m.name[m.name.length - 1];
        });

        this.loadGobWeightHistoryReport();
        this.loadScales();
      });
  }

  private getPlantTime(localTime: Date) {
    return DateUtilityService.localToTimeZone(localTime, this.timeZoneId).toDate();
  }

  private getLocalTime(plantTime: Date) {
    return DateUtilityService.timeZoneToLocal(plantTime, this.timeZoneId).toDate();
  }

  private formatChart() {
    let scope = this.api.getScope();
    this.api.refresh();

    if (scope) {
      let chart = <nv.LineChart>scope.chart;
      chart.lines.dispatch.on('elementClick', e => {
        this.viewWeightDetail(e);
      });
    }
    scope.chart.yScale = d3.scale.linear().domain([this.getGlobalMinWeight(), this.getGlobalMaxWeight()])
      .range([this.height * 0.85, this.height * 0.15]);

    this.updatePointCssClasses();
    let elems = document.getElementsByClassName('nv-legend-symbol');
    let observer = new MutationObserver((mutations) => {
      mutations.forEach((mutationRecord) => {
        this.updatePointCssClasses();
      });
    });
    for (let ctr = 0; ctr < elems.length; ctr++) {
      observer.observe(elems[ctr], { attributes: true, attributeFilter: ['style'] });
    }
  }

  private getGlobalMinWeight() {
    return d3.min(this.gobWeightsPretty, s => d3.min(s.values, (w: DataPoint) => w.y));
  }

  private getGlobalMaxWeight() {
    return d3.max(this.gobWeightsPretty, s => d3.max(s.values, (w: DataPoint) => w.y));
  }

  private initZoom() {
    if (!this.allowZoom) {
      return;
    }

    let scope = this.api.getScope();
    let xDomain = scope.chart.xDomain || this.xScale.domain;
    let yDomain = scope.chart.yDomain || this.yScale.domain;
    let unzoomEventType = 'dblclick.zoom';

    let d3zoom;
    let zoomed;
    let unzoomed;
    let self = this;

    let rect = d3.select('rect');
    let width: number = parseInt(rect.attr('width'), 10);
    let height: number = parseInt(rect.attr('height'), 10);
    let xBoundary = d3.time.scale().domain([this.start, this.end]).range([0, width]);
    let yBoundary = d3.scale.linear().domain([this.getGlobalMinWeight(), this.getGlobalMaxWeight()])
      .range([height * 0.85, height * 0.15]);

    if (this.zoomLevel === 1 || !this.xScale || !this.yScale) {
      this.xScale = xBoundary;
      this.yScale = yBoundary;
    }

    // zoom event handler
    zoomed = () => {
      // record current translation and scale; we only want to refresh the full chart if we're not zoomed
      // or panned.
      self.zoomLevel = (<d3.ZoomEvent>d3.event).scale;

      xDomain(self.xScale.domain());
      yDomain(self.yScale.domain());

      if (scope.chart) {
        scope.chart.update();
      }
    };

    // unzoomed event handler
    unzoomed = () => {
      self.zoomLevel = (<d3.ZoomEvent>d3.event).scale;
      xDomain([self.start, self.end]);
      yDomain([self.getGlobalMinWeight(), self.getGlobalMaxWeight()]);

      d3zoom.scale(self.scale).translate(self.translate);
      if (scope.chart) {
        scope.chart.update();
      }
    };

    // create d3 zoom handler
    d3zoom = d3.behavior.zoom()
      .x(this.xScale)
      .y(this.yScale)
      .scaleExtent(this.scaleExtent)
      .on('zoom', zoomed);

    if (scope.svg) {
      scope.svg.call(d3zoom);

      d3zoom.scale(this.scale).translate(this.translate);

      if (this.zoomLevel === 1) {
        d3zoom.event(scope.svg);
      }

      scope.svg.on(unzoomEventType, unzoomed);
    }
  }

  private loadGobWeightHistoryReport(): angular.IPromise<any> {
    this.chartInitialized = false;

    if (this.chartIntervalPromise) {
      this.$interval.cancel(this.chartIntervalPromise);
      this.chartIntervalPromise = null;
    }

    return this.gobWeightsService.getGobWeightHistoryReport(this.shopId, this.start, this.end)
      .then(weightChart => {
        this.gobWeightsReport = weightChart;
        this.prepareGobWeightsForChart();
        this.formatChart();

        // If we're dealing with a relative timeframe (e.g. past 24 hours), update the chart's frame of
        // reference on an interval
        if (this.isRelativeToNow) {
          this.chartIntervalPromise = this.$interval(() => this.updateRelativeTimeframe(), this.chartUpdateInterval);
        }
      });
  }

  addIncomingGobWeight(weight: apsViewModels.GobWeightItem): void {
    if (weight.shopId !== this.shopId) {
      return;
    }

    if (!this.isRelativeToNow) {
      let time = weight.timeWeighed.getTime();
      if (time < this.start.getTime() || time >= this.end.getTime()) {
        return;
      }
    }

    this.gobWeightsReport.gobWeights.push(weight);
    this.updateRelativeTimeframe();
    this.updatePointCssClasses();
  }

  addIncomingGobWeightComment(comment: apsEvents.GobWeightCommentAdded): void {
    if (comment.shopId !== this.shopId) {
      return;
    }

    let commentTime = comment.createdOn.getTime();
    let weight = _.find(this.gobWeightsReport.gobWeights,
      w => w.timeWeighed.getTime() === commentTime
        && w.gob === comment.gob
        && w.isMachineId === comment.isMachineId);
    if (!weight) {
      return;
    }

    if (_.some(weight.comments, c => c.createdOn.getTime() === commentTime)) {
      return;
    }

    weight.comments.push(comment);
    this.prepareGobWeightsForChart();
  }

  private updateRelativeTimeframe(): void {
    if (!this.isRelativeToNow) {
      return;
    }

    let end = moment();
    this.end = end.toDate();
    this.start = end.subtract(this.hoursToShow, 'hours').toDate();

    this.pruneExpiredData();

    this.initZoom();
  }

  private pruneExpiredData() {
    _.remove(this.gobWeightsReport.gobWeights, w => w.timeWeighed < this.start);
    this.prepareGobWeightsForChart();
  }

  changeWeightHours(hours: number): void {
    if (hours === this.hoursToShow) {
      return;
    }

    this.isRelativeToNow = true;
    this.start = moment().subtract(hours, 'hours').toDate();
    this.end = moment().toDate();
    this.hoursToShow = hours;
    this.loadGobWeightHistoryReport();
  }

  applyCustomRange() {
    this.isRelativeToNow = false;
    this.hoursToShow = null;
    this.loadGobWeightHistoryReport();
  }

  private getFriendlyTimespanName(): string {
    let name: string = (this.isRelativeToNow) ? 'Past ' : '';
    let start: moment.Moment = moment(this.start);
    let end = this.end ? moment(this.end) : moment();
    let span = moment.duration(end.diff(start));

    name += span.humanize(true);
    return name;
  }

  private prepareGobWeightsForChart(): void {
    let gobs = [];
    let numericGobs = [];

    let colorIndex = 0;
    let colors = ['#1F77B4', '#FF7F0E', '#0F645F', '#800080', '#20B2AA', '#B22222'];

    for (let w of this.gobWeightsReport.gobWeights) {
      var name = w.gobName;
      if (this.shop.machineConfigurations.length > 1) {
        name = this.machineNameMap[w.isMachineId] + '-' + w.gobName;
      }

      if (!gobs[name]) {
        gobs[name] = {
          values: [],
          key: name
        };

        if (colors.length >= colorIndex + 1) {
          gobs[name].color = colors[colorIndex++];
        }
      }

      gobs[name].values.unshift({
        instance: w,
        x: w.timeWeighed,
        y: w.weight.toFixed(2)
      });
    }
    // Add target, warn, and alert lines
    let boundaries = [
      { name: 'Target', key: 'weightTarget', color: 'green', calc: (w: any) => w.target },
      { name: 'Alarm Low', key: 'weightAlarmLower', color: 'red', calc: (w: any) => w.target * (1.0 - w.alarmMargin) },
      { name: 'Alarm High', key: 'weightAlarmUpper', color: 'red', calc: (w: any) => w.target * (1.0 + w.alarmMargin) },
      { name: 'Warn Low', key: 'weightWarningLower', color: 'orange', calc: (w: any) => w.target * (1.0 - w.warnMargin) },
      { name: 'Warn High', key: 'weightWarningUpper', color: 'orange', calc: (w: any) => w.target * (1.0 + w.warnMargin) },
      { name: ' ', key: 'top', color: 'white', calc: (w: any) => w.target * (1.0 + w.alarmMargin * 1.1) },
      { name: '  ', key: 'bottom', color: 'white', calc: (w: any) => w.target * (1.0 - w.alarmMargin * 0.9) }
    ];

    for (let b of boundaries) {
      gobs[b.name] = {
        values: [],
        key: b.name,
        color: b.color
      };

      for (let t of this.gobWeightsReport.weightTargets) {
        gobs[b.name].values.push({
          x: t.startDate < this.start ? this.start : t.startDate,
          y: t[b.key]
        }, {
          x: (t.endDate && t.endDate < this.end) ? t.endDate : this.end,
          y: t[b.key]
        });
        if (b.key === 'top') {
          gobs[b.name].values.push({
            x: t.startDate < this.start ? this.start : t.startDate,
            y: t.weightTarget + (t.weightAlarmUpper - t.weightTarget) * 1.5
          }, {
            x: (t.endDate && t.endDate < this.end) ? t.endDate : this.end,
            y: t.weightTarget + (t.weightAlarmUpper - t.weightTarget) * 1.5
          });
        }
        if (b.key === 'bottom') {
          gobs[b.name].values.push({
            x: t.startDate < this.start ? this.start : t.startDate,
            y: t.weightTarget - (t.weightTarget - t.weightAlarmLower) * 1.5
          }, {
            x: (t.endDate && t.endDate < this.end) ? t.endDate : this.end,
            y: t.weightTarget - (t.weightTarget - t.weightAlarmLower) * 1.5
          });
        }
      }
    }

    // Change from an associative array (object) into a numerically-indexed (real) one;
    // this is expected by the nvd3 chart directive
    for (let g in gobs) {
      gobs[g].values = _.sortBy(gobs[g].values, (w: DataPoint) => w.x);
      numericGobs.push(gobs[g]);
    }

    numericGobs = _.sortBy(numericGobs, g => {
      let value = 0;

      if (g.key.length > 3) {
        // It's a boundary; set its sort value arbitrarily high
        value = 100000;
      } else {
        // It's a weight data series (name in form of {machine}-{gob}). Machines will be naturally ordered A-Z; gobs
        // F and M are well-ordered relative to each other, but B ('Back') needs to be last - special-case it.
        if (g.key.length >= 3) {
          value += g.key[0].charCodeAt(0) * 500;
          value += g.key[2] === 'B' ? 200 : g.key[2].charCodeAt(0);
        } else {
          value += g.key[0] === 'B' ? 200 : g.key[0].charCodeAt(0);
        }
      }
      return value;
    });

    this.gobWeightsPretty = numericGobs;
  }

  updatePointCssClasses(): void {
    let points = d3.selectAll('.nv-point')[0];

    for (let p of points) {
      let d = d3.select(p).data()[0][0];

      if (!d.instance) {
        continue;
      }
      d3.select(p).classed('nv-point-comments', d.instance.comments && d.instance.comments.length > 0);
      d3.select(p).classed('nv-point-nocomments', !d.instance.comments || d.instance.comments.length === 0);
    }
  }

  private viewWeightDetail(weight: any): void {
    let chart = <nv.LineChart>this.api.getScope().chart;
    chart.tooltip.hidden(true);

    let userPromise = this.currentUser ? this.$q.when(this.currentUser) : this.authService.getUserData();
    userPromise.then(user => {
      this.currentUser = user;
      let result = openGobWeightDetail({
        plantId: this.shop.plantId,
        shop: this.shop,
        weight: weight.point.instance,
        user: this.currentUser
      }, this.$modal);

      result.then(resultData => {
        if (resultData.gobWeightDeleted) {
          let deletedWeight = weight.point.instance;
          _.remove(this.gobWeightsReport.gobWeights, deletedWeight);
        }
        if (resultData.commentsAdded || resultData.gobWeightDeleted) {
          this.prepareGobWeightsForChart();
          this.updatePointCssClasses();
        }
        chart.lines.clearHighlights();
      });
    });
  }

  private setMode(instance: Scale, newMode: apsEnums.ScaleMode) {
    instance.scaleConfig.Mode = newMode;
    instance.modeName = apsEnums.ScaleMode[newMode];
    instance.availableModes = this.scaleModes.filter(x => x.value !== newMode);
  }

  private loadScales(): void {
    this.equipmentService.getScales(
      this.shop.plantId,
      this.shop.id
    ).then((scaleList: Scale[]) => {
      this.scales = scaleList;
      this.scales.forEach((s: Scale) => {
        s.scaleConfig = JSON.parse(s.config);
        this.setMode(s, s.scaleConfig.Mode);
      });
    });
  }

  toggleScaleMode(instance: Scale, newMode: apsEnums.ScaleMode): void {
    this.loaderService.loadingPromise(this.equipmentService.setScaleMode({
      id: instance.id,
      plantId: this.shop.plantId,
      mode: newMode
    }).then(() => { this.setMode(instance, newMode); }, this.notifyService.handleApiErrors));
  }

}

export default angular.module('aps.controller.gobWeightsGraph', requires)
  .controller('GobWeightsGraphCtrl', GobWeightsGraphCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/dashboard/gobWeightsGraph.controller.ts